<template>
  <div class="admin-welcome">
    <div v-if="!Array.isArray(companyList) || companyList.length === 0" class="d-flex justify-content-center mt-5 w-100">
      <loading-spinner/>
    </div>
    <div v-else>
        <b-card class="my-2" body-class=" d-flex justify-content-between align-items-middle">
          <div style="max-width: 420px">
            <b-input placeholder="Search company..." v-model="searchTerm" />
          </div>
          <div>
            <b-dropdown id="dropdown-1" text="Create Ticket" variant="outline-primary">
              <b-dropdown-item href="https://reallinks.atlassian.net/jira/core/projects/RLET/form/1?atlOrigin=eyJpIjoiNGFmZjU1OTJkYTEwNDUyZThiNjQ1MWQzMjQ5Y2ExOTIiLCJwIjoiaiJ9" target="_blank">🐞 Report a bug</b-dropdown-item>
              <b-dropdown-item href="https://reallinks.atlassian.net/jira/core/projects/RLET/form/2?atlOrigin=eyJpIjoiZjUzNjRmOTU4YjMxNGJiZmE4NTQ2NjEzNTMxNjBlNDAiLCJwIjoiaiJ9" target="_blank">🤝 Create support ticket</b-dropdown-item>
              <b-dropdown-item href="https://reallinks.atlassian.net/jira/core/projects/RLET/form/3?atlOrigin=eyJpIjoiYzM4NjFiNGMwZjAzNDJmNzljZDFmZTViZWEwZDE1YjkiLCJwIjoiaiJ9" target="_blank">🛠 Add a configuration task</b-dropdown-item>
              <b-dropdown-item href="https://reallinks.atlassian.net/jira/core/projects/RLET/form/4?atlOrigin=eyJpIjoiMTI5NWM2YTIxNmQ1NDg0M2IyYTM0YmMxMjA3YTE3ZDUiLCJwIjoiaiJ9" target="_blank">💡 Add a product idea</b-dropdown-item>
            </b-dropdown>
            <b-button variant="primary" style="color: #fff !important" href="https://reallinks.atlassian.net/jira/dashboards/10003" target="_blank">See Tickets</b-button>
          </div>
        </b-card>
        <b-table class="border" :fields="companyFields" :items="filteredCompanyList" responsive="sm">
          <template #cell(name)="{ value, item: { id: companyID, handle, smallLogo, primaryColour } }">
            <router-link class="d-flex flex-row justify-content-start align-items-center text-decoration-none ml-2"
              :to="{ name: 'CompanyLogin', params: { companyID } }" :id="companyID">
                <img v-if="smallLogo" :src="smallLogo" height="20px" width="20px"/>
                <b-img v-else blank :blank-color="primaryColour" class="rounded-circle" height="20px" width="20px"/>
                <span class="font-weight-bold text-black ml-2">{{ value }} <small v-if="handle">({{ handle }})</small></span>
            </router-link>
          </template>
          <template #cell(links)="{ item: { id: companyID, notionURL, jiraDashboardURL } }">
            <div class="d-flex flex-row justify-content-start align-items-center">
              <router-link :to="{ name: 'CompanySignin', params: { companyID } }" target="_blank">
                <font-awesome-icon icon="globe" size="lg" v-b-tooltip.hover title="Visit Site"/>
              </router-link>
              <a v-if="notionURL" class="ml-2" :href="notionURL" target="_blank">
                <img src="@/assets/notion.png" height="24px" width="24px" v-b-tooltip.hover title="Notion"/>
              </a>
              <a v-if="jiraDashboardURL" class="ml-2" :href="jiraDashboardURL" target="_blank">
                <img src="@/assets/jira.png" height="24px" width="24px" v-b-tooltip.hover title="Jira Dashboard"/>
              </a>
              <router-link class="ml-2" :to="{ name: 'JobIndexTaxonomy', params: { companyID } }" target="_blank">
                <font-awesome-icon icon="briefcase" size="lg" v-b-tooltip.hover title="Job Taxonomy"/>
              </router-link>
              <a class="ml-2" :href="`/${companyID}/login?next=/${companyID}/playground`" target="_blank" v-if="isDeveloper">
                <img src="@/assets/graphql.svg" height="20px" width="20px" v-b-tooltip.hover title="GraphQL Playground"/>
              </a>
            </div>
          </template>
          <template #cell(flags)="{ item: { hasApp, canSyncContacts, hasLeaderboards, hasRewards, hasSlack } }">
            <div class="d-flex flex-row justify-content-start align-items-center pt-1">
              <font-awesome-icon v-if="canSyncContacts" class="mr-2" icon="user-check" size="1x" v-b-tooltip.hover title="Can Sync Contacts"/>
              <font-awesome-icon v-if="hasLeaderboards" class="mr-2" icon="list" size="1x" v-b-tooltip.hover title="Has Leaderboards"/>
              <font-awesome-icon v-if="hasRewards" class="mr-2" icon="store" size="1x" v-b-tooltip.hover title="Has Rewards"/>
              <font-awesome-icon v-if="hasApp" class="mr-2" icon="mobile" size="1x" v-b-tooltip.hover title="Has App"/>
              <img v-if="hasSlack" class="mr-2" src="@src/assets/integrations/slack-mark.png" height="15px" width="15px"
                v-b-tooltip.hover title="Has Slack"/>
            </div>
          </template>
          <template #cell(status)="{ value }">
            <b-img v-if="value === 'ACTIVE'" blank blank-color="#28a745" class="rounded-circle" height="20px" width="20px" v-b-tooltip.hover.topleft title="Active"/>
            <b-img v-else-if="value === 'INACTIVE'" blank blank-color="#ffc107" class="rounded-circle" height="20px" width="20px" v-b-tooltip.hover.topleft title="Inactive"/>
            <b-img v-else-if="value === 'DELETED'" blank blank-color="#dc3545" class="rounded-circle" height="20px" width="20px" v-b-tooltip.hover.topleft title="Deleted"/>
            <b-img v-else blank blank-color="#6c757d" class="rounded-circle" height="20px" width="20px" v-b-tooltip.hover.topleft title="Unknown"/>
          </template>
        </b-table>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from '@src/components/misc/LoadingSpinner';
import { mapAdmin } from '@src/layouts/Admin';

export default {
  components: {
    LoadingSpinner,
  },
  metaInfo: {
    title: 'Welcome',
  },
  data() {
    return {
      searchTerm: '',
    };
  },
  computed: {
    ...mapAdmin([ 'companyList', 'isDeveloper' ]),
    companyFields: () => ([
      { key: 'name', tdAttr: { style: 'min-width: 500px' } },
      { key: 'links', label: 'Links' },
      { key: 'flags', label: 'Features' },
      { key: 'status' },
    ]),
    filteredCompanyList() {
      const { companyList, searchTerm } = this;
      console.log({ companyList, searchTerm });
      return (Array.isArray(companyList) ? companyList : []).filter(({ name }) => {
        if (searchTerm.length < 3) return true;
        return name.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
  },
  mounted() {
    this.$root.admin.$once('admin-companyListFetched', () => {
      if (this.companyList.length === 1) {
        const [ { id: companyID } ] = this.companyList;
        this.$router.replace({ name: 'CompanyLogin', params: { companyID } });
      }
    });
  },
};
</script>

<style lang="scss">
.admin-welcome {
  & > .row {
    display: flex;
    flex-wrap: wrap;

    & > div[class*='col-'] {
      display: flex;
    }
  }

  a {
    color: inherit !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Admin/Welcome.vue